'use client';

import React from 'react';
import cn from 'classnames';
import styles from './iconLink.module.scss';
import { getTopicIcon, navigateToAnchor, navigateToLink } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';

export interface IconLinkProps {
  readonly title: string;
  readonly icon: string;
  readonly link?: string;
  readonly id?: string;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly ariaLabel?: string;
  readonly ariaDescribedBy?: string;
}

const IconLink = ({ title, icon, link, id, className, onClick, ariaLabel, ariaDescribedBy }: IconLinkProps): React.ReactElement => {
  const router = useRouter();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (onClick) {
      onClick();
      return;
    }

    if (link) {
      // Handle external links (http or https)
      navigateToLink(link, router);
    } else if (id) {
      navigateToAnchor(id, router);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  // Determine appropriate href for the link
  const href = link || (id ? `#${id}` : '#');

  return (
    <a
      href={href}
      className={cn(styles.link, className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel || title}
      aria-describedby={ariaDescribedBy}
    >
      <span className={styles.icon} aria-hidden="true">
        {getTopicIcon(icon)}
      </span>
      <span className={styles.title}>{title}</span>
    </a>
  );
};

export default IconLink;
